package ai.flowstorm.studio.ui.design.dialogue.graph

import ai.flowstorm.studio.model.Graph.Intent
import ai.flowstorm.studio.resources.space.DialogueMixinResource
import ai.flowstorm.studio.ui.design.dialogue.GraphEditor
import kotlinx.coroutines.launch
import ai.flowstorm.common.ui.CodeEditor
import ai.flowstorm.common.ui.Icon
import ai.flowstorm.common.ui.UiHelpers
import ai.flowstorm.common.ui.form.ListIdSelect
import ai.flowstorm.studio.ui.design.AlternativesModal
import io.kvision.core.onEvent
import io.kvision.form.check.CheckBox
import io.kvision.form.spinner.Spinner
import io.kvision.form.text.Text
import io.kvision.html.Button
import io.kvision.i18n.I18n.gettext
import io.kvision.i18n.I18n.tr
import io.kvision.panel.simplePanel

class IntentEditor(graphEditor: GraphEditor, val global: Boolean = false) : NodeEditor<Intent>(graphEditor), EditableCode {
    private val alternativesPrompt = "Generate #NUM# alternative sentences."
    init {
        graphEditor.subscribe {
            mainScope.launch {
                mixinSelect.options = UiHelpers.options(DialogueMixinResource.load("Intent", it.language))
            }
        }
    }

    val mixinSelect = ListIdSelect(label = tr("Mixins")).apply {
        mainScope.launch {
            options = UiHelpers.options(DialogueMixinResource.load("Intent", graphEditor.state.language))
        }
        onEvent {
            change = {
                mainScope.launch {
                    codeEditor.set(codeEditor.value, this@apply.getValue() ?: listOf())
                }
            }
        }
    }

    override val codeEditor = CodeEditor("flowstorm").apply {
        onEvent {
            change = {
                if (node!!.utterances.joinToString("\n") != self.value) {
                    node?.utterances = self.value.lines()
                    graphEditor.commit(gettext("Intent utterances changed") + " [#${node!!.id}]", "intent[${node!!.id}].utterances")
                }
            }
        }
    }

    private val exampleAlternativesButton = Button("Alternatives").onClick {
        mainScope.launch {
            val n = (node!!.utterances.size + 10).toString()
            val alternatives = AlternativesModal(alternativesPrompt.replace("#NUM#", n)).open(node!!.utterances)
            node!!.utterances += alternatives
            codeEditor.set(node!!.utterances.joinToString("\n"), node!!.mixins_id)
        }
    }

    val negUtterancesEditor = CodeEditor("flowstorm").apply {
        onEvent {
            change = {
                if (node!!.negativeUtterances.joinToString("\n") != self.value) {
                    node?.negativeUtterances = self.value.lines()
                    graphEditor.commit(gettext("Intent negative utterances changed") + " [#${node!!.id}]", "intent[${node!!.id}].negativeUtterances")
                }
            }
        }
    }

    private val negativeExamplesAlternativesButton = Button("Alternatives").onClick {
        mainScope.launch {
            val n = (node!!.negativeUtterances.size + 10).toString()
            val alternatives = AlternativesModal(alternativesPrompt.replace("#NUM#", n)).open(node!!.negativeUtterances)
            node!!.negativeUtterances += alternatives
            negUtterancesEditor.set(node!!.negativeUtterances.joinToString("\n"))
        }
    }

    override var readOnly: Boolean
        get() = super.readOnly
        set(value) {
            super.readOnly = value
            codeEditor.readOnly = value
            negUtterancesEditor.readOnly = value
            exampleAlternativesButton.disabled = value
            negativeExamplesAlternativesButton.disabled = value
        }

    override val form = object : NodeForm(Intent.serializer()) {
        init {
            add(
                    Intent::label,
                    Text(label = tr("Label")),
            )
            add(Intent::name, Text(label = tr("Name")),
                    required = true,
                    validatorMessage = { gettext("Node name must be unique.") },
                    validator = { text ->
                        !graphEditor.graph.nodes.any { it.name == text.value && it != node }
                    }
            )
            add(
                    Intent::mixins_id,
                    mixinSelect

            )
            add(Intent::threshold, Spinner(label = tr("Threshold"), min = 0, max = 1, step = 0.01, decimals = 2).apply {
                onEvent {
                    change = { if (validate()){ save() } }
                }
            })
            add(Intent::entities, Text(label = tr("Entities")))
            if (!global) {
                add(Intent::suggested, CheckBox(label = tr("Include in suggestions")))
            }
        }
    }

    val examples = simplePanel("examples-editor") {
        this.add(codeEditor)
        this.add(exampleAlternativesButton)
    }

    val negativeExamples = simplePanel( "examples-editor") {
        this.add(negUtterancesEditor)
        this.add(negativeExamplesAlternativesButton)
    }

    override val tabPanel = object : NodeTabPanel() {
        init {
            addTab(tr("Properties"), form, Icon.Properties.def)
            addTab(tr("Examples"), examples, Icon.Text.def)
            addTab(tr("Negative Examples"), negativeExamples, Icon.Strikethrough.def)
            activeIndex = 1
        }

        override fun tabInit(index: Int) = when (index) {
            1 -> codeEditor.focus()
            else -> super.tabInit(index)
        }
    }

    override fun load(node: Intent) {
        super.load(node)
        form.load(node)
        codeEditor.set(node.utterances.joinToString("\n"), node.mixins_id)
        negUtterancesEditor.set(node.negativeUtterances.joinToString("\n"))
    }
}
